import { useEffect, useRef, useCallback } from "react";

export interface ArSessionExitParams {
  durationSeconds: number;
}

export interface ArSessionTimerConfig {
  isActive: boolean;
  hasExited: boolean;
  onEnteringXR?: () => void;
  onExitingXR?: (params: ArSessionExitParams) => void;
}

/**
 * AR session timer with visibility awareness.
 */
export function useArSessionTimer({
  isActive,
  hasExited,
  onEnteringXR,
  onExitingXR,
}: ArSessionTimerConfig) {
  const startTimeRef = useRef(0);
  const activeTimeRef = useRef(0);
  const sessionSentRef = useRef(false);
  const wasActiveRef = useRef(false);

  const endSession = useCallback(() => {
    if (sessionSentRef.current || startTimeRef.current === 0) return;

    if (document.visibilityState === "visible" && startTimeRef.current > 0) {
      activeTimeRef.current += Date.now() - startTimeRef.current;
    }

    const durationSeconds = Math.round(activeTimeRef.current / 1000);
    onExitingXR?.({ durationSeconds });

    sessionSentRef.current = true;
    startTimeRef.current = 0;
    activeTimeRef.current = 0;
  }, [onExitingXR]);

  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      startTimeRef.current = Date.now();
      activeTimeRef.current = 0;
      sessionSentRef.current = false;
      wasActiveRef.current = true;
      onEnteringXR?.();
    }

    if (!isActive && wasActiveRef.current) {
      wasActiveRef.current = false;
    }
  }, [isActive, onEnteringXR]);

  useEffect(() => {
    if (hasExited) endSession();
  }, [hasExited, endSession]);

  useEffect(() => {
    if (!isActive) return;

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        if (startTimeRef.current > 0) {
          activeTimeRef.current += Date.now() - startTimeRef.current;
          startTimeRef.current = 0;
        }
      } else if (!sessionSentRef.current) {
        startTimeRef.current = Date.now();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;

    window.addEventListener("pagehide", endSession);
    return () => window.removeEventListener("pagehide", endSession);
  }, [isActive, endSession]);

  return { endSession };
}
